import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputComponent } from '../../../shared/input/input.component';
import { InputSelectComponent } from '../../../shared/input-select/input-select.component';
import { RowComponent } from '../../../shared/row/row.component';
import { Organization } from '../../../model/organization/organization.model';
import countriesData from '../../../../../countries.json';
import {InputMultiselectComponent} from "../../../shared/input-multiselect/input-multiselect.component";
import {Region} from "../../../model/region/region.model";
import {RegionService} from "../../../model/region/region.service";

@Component({
  selector: 'app-user-form-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputComponent,
    InputSelectComponent,
    RowComponent,
    InputMultiselectComponent
  ],
  templateUrl: './user-form-modal.component.html',
  styleUrls: ['./user-form-modal.component.scss']
})
export class UserFormModalComponent implements OnInit {
  @Input() allRoles: { label: string; value: string }[] = [];
  @Input() regions: Region[] = [];
  @Output() save = new EventEmitter<any>();
  filteredOrganizations: Organization[] = [];
  visible = false;
  isNewUser = true;
  userForm: FormGroup;
  countryOptions = countriesData.map(item => ({ label: item.name, value: item.code }));

  private regionService = inject(RegionService);

  constructor(private fb: FormBuilder) {
    this.userForm = this.fb.group({
      id: [null],
      firstName: [null, Validators.required],
      lastName: [null, Validators.required],
      phone: [
        null,
        [Validators.required, Validators.pattern(/^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/)]
      ],
      email: [null, [Validators.required, Validators.email]],
      callSign: [null],
      unit: [null],
      roles: [null],
      regionId: [null],
      organizationId: [{disabled: true}]
    });
  }

  ngOnInit(): void {
    this.userForm.get('organizationId').disable()
  }

  async loadOrganizationsForRegion(): Promise<void> {
    const regionId = this.userForm.get('regionId').value
    if (regionId) {
      try {
        this.filteredOrganizations = await this.regionService.fetchAllForRegionId(regionId);
        this.userForm.get('organizationId').enable();
      } catch (err) {
        throw err
      }
    } else {
      this.userForm.get('organizationId').disable();
    }
  }

  show(user?: any) {
    if (user) {
      this.isNewUser = false;
      this.userForm.patchValue({ ...user, roles: Array.isArray(user.roles) ? user.roles : null });
      this.userForm.get('roles')?.clearValidators();
      this.userForm.get('roles')?.updateValueAndValidity();
      this.userForm.get('regionId')?.clearValidators();
      this.userForm.get('regionId')?.updateValueAndValidity();
      this.userForm.get('organizationId')?.clearValidators();
      this.userForm.get('organizationId')?.updateValueAndValidity();
    } else {
      this.isNewUser = true;
      this.userForm.reset();
      this.userForm.get('roles')?.setValidators([Validators.required]);
      this.userForm.get('roles')?.updateValueAndValidity();
      this.userForm.get('regionId')?.setValidators([Validators.required]);
      this.userForm.get('regionId')?.updateValueAndValidity();
      this.userForm.get('organizationId')?.setValidators([Validators.required]);
      this.userForm.get('organizationId')?.updateValueAndValidity();
    }
    this.visible = true;
  }

  hide() {
    this.visible = false;
  }

  onSubmit() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.save.emit({ ...this.userForm.value });
    this.userForm.get('organizationId').disable()
    this.hide();
  }

  onCancel() {
    this.userForm.get('organizationId').disable()
    this.hide();
  }
  
  getDialogHeader(): string {
    return this.isNewUser
      ? $localize`:@@createAccountHeader:Create Account`
      : $localize`:@@editAccountHeader:Edit Account`;
  }
}
